import { Component } from '@theme/component';

class MtMegaMenu extends Component {
  #resizeObserver;
  #accordionAnimations = new Map();

  connectedCallback() {
    super.connectedCallback();
    this.#observeSize();
    this.addEventListener('keyup', this.#handleEscape);
  }

  updatedCallback() {
    this.#finishAccordionAnimations();
    super.updatedCallback();
    this.#observeSize();
  }

  disconnectedCallback() {
    this.#finishAccordionAnimations();
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
    this.removeEventListener('keyup', this.#handleEscape);
  }

  #observeSize() {
    this.#resizeObserver?.disconnect();
    if (this.dataset.layout === 'mobile') return;
    this.#resizeObserver = new ResizeObserver(() => {
      this.dispatchEvent(new CustomEvent('mt-menu-resize', { bubbles: true }));
    });
    this.#resizeObserver.observe(this);
  }

  selectCategory(event) {
    const tab = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
    if (!(tab instanceof HTMLButtonElement)) return;
    const tabs = [...this.querySelectorAll('[role="tab"]')];
    tab.closest('[role="tablist"]')?.style.setProperty('--mt-group-active-index', String(tabs.indexOf(tab)));
    for (const categoryTab of tabs) {
      const selected = categoryTab === tab;
      categoryTab.setAttribute('aria-selected', String(selected));
      categoryTab.setAttribute('tabindex', selected ? '0' : '-1');
    }
    for (const panel of this.querySelectorAll('[role="tabpanel"]')) {
      panel.hidden = panel.id !== tab.getAttribute('aria-controls');
    }
  }

  navigateCategories(event) {
    const tabs = [...this.querySelectorAll('[role="tab"]')];
    const currentTab = event.target;
    const currentIndex = tabs.indexOf(currentTab);
    let nextIndex;
    if (event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % tabs.length;
    if (event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = tabs.length - 1;
    if (nextIndex === undefined) return;
    event.preventDefault();
    tabs[nextIndex]?.focus();
  }

  toggleAccordion(event) {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement)) return;
    if (!details.open) this.#cancelAccordionAnimation(details);
    if (this.#accordionAnimations.has(details)) return;
    details.querySelector(':scope > summary')?.setAttribute('aria-expanded', String(details.open));
    if (!details.open) return;
    this.#closeSiblingAccordions(details);
  }

  animateAccordion(event) {
    const details = event.target instanceof Element ? event.target.closest('.mt-header-mobile__details') : null;
    if (!(details instanceof HTMLDetailsElement)) return;
    event.preventDefault();
    const open = !(this.#accordionAnimations.get(details)?.open ?? details.open);
    this.#animateAccordion(details, open);
    if (open) this.#closeSiblingAccordions(details);
  }

  #closeSiblingAccordions(details) {
    for (const sibling of details.parentElement?.parentElement?.children ?? []) {
      const other = sibling.querySelector(':scope > details');
      if (other instanceof HTMLDetailsElement && other !== details && other.open) {
        this.#animateAccordion(other, false);
      }
    }
  }

  #animateAccordion(details, open) {
    const summary = details.querySelector(':scope > summary');
    const content = details.querySelector(':scope > .mt-header-mobile__children');
    if (!summary || !content) return;
    const startHeight = details.open ? content.getBoundingClientRect().height : 0;
    const startOpacity = details.open ? getComputedStyle(content).opacity : '0';
    this.#cancelAccordionAnimation(details);
    summary.setAttribute('aria-expanded', String(open));
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !content.animate) {
      details.open = open;
      return;
    }
    if (open) details.open = true;
    if (!details.open) return;
    details.setAttribute('data-animating', '');
    const animation = content.animate(
      [
        { height: `${startHeight}px`, opacity: startOpacity },
        { height: `${open ? content.scrollHeight : 0}px`, opacity: open ? 1 : 0 },
      ],
      { duration: 220, easing: 'ease-out', fill: 'both' }
    );
    this.#accordionAnimations.set(details, { animation, open });
    animation.onfinish = () => {
      details.open = open;
      this.#cancelAccordionAnimation(details);
    };
  }

  #cancelAccordionAnimation(details) {
    const state = this.#accordionAnimations.get(details);
    if (!state) return;
    state.animation.onfinish = null;
    state.animation.cancel();
    details.removeAttribute('data-animating');
    this.#accordionAnimations.delete(details);
  }

  #finishAccordionAnimations() {
    for (const [details, state] of this.#accordionAnimations) {
      details.open = state.open;
      this.#cancelAccordionAnimation(details);
    }
  }

  #handleEscape = (event) => {
    if (event.key !== 'Escape' || this.dataset.layout !== 'mobile') return;
    const details = event.target instanceof Element ? event.target.closest('.mt-header-mobile__details[open]') : null;
    if (!(details instanceof HTMLDetailsElement)) return;
    event.stopPropagation();
    this.#animateAccordion(details, false);
    details.querySelector(':scope > summary')?.focus();
  };
}

if (!customElements.get('mt-mega-menu')) {
  customElements.define('mt-mega-menu', MtMegaMenu);
}
